import { useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import Api from '../Api';
import { API_ERROR } from '../constants/error.constants';
import { auth0SilentTokenOptions } from '../constants/global.constants';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { isInvalidRefreshTokenError } from '~/lib/util/auth.util';

const getError = (
  error:
    | string
    | { message: string; _embedded: { errors: { message: string }[] } }
    | undefined,
): string => {
  if (typeof error === 'string') return error;
  if (typeof error?._embedded?.errors?.[0]?.message === 'string') {
    return error._embedded.errors[0].message;
  }
  if (typeof error?.message === 'string') return error?.message;
  return API_ERROR;
};

/**
 * @deprecated Please use RTK Query for new API calls
 */
const useApi = <T,>(): UseApiHookState<T> => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { logError } = useDatadog();
  const defaultState: UseApiState<T> = {
    data: null,
    didTimeOut: false,
    error: null,
    loading: false,
    statusCode: null,
  };

  const [state, setState] = useState<UseApiState<T>>(defaultState);

  const resetError = (): void => {
    setState({ ...state, error: defaultState.error });
  };

  const resetData = (): void => {
    setState({ ...state, data: defaultState.data });
  };

  /**
   * Hook function to make the HTTP request to our API Gateway.
   *
   * @param command The command to execute in the request (GET, POST, PATCH, etc.).
   * @param url The relative path to our services, relative to the base URL of the API Gateway.
   * @param options Object containing either query params for GET requests or `body` of a POST request.
   * @param mock Flag to determine whether to use a mock API call or our actual API backend.
   * @param callback A callback called when transaction completes.
   */
  const sendRequest = ({
    command,
    url,
    options = {},
    mock = false,
    callback,
  }: SendRequestParams<T>): void => {
    setState({
      ...defaultState,
      loading: true,
    });

    (async () => {
      const newState: UseApiState<T> = {
        ...defaultState,
        loading: false,
      };

      let accessToken: string;
      try {
        accessToken = await getAccessTokenSilently(auth0SilentTokenOptions);
      } catch (error) {
        if (isInvalidRefreshTokenError(error)) {
          return await logout({
            logoutParams: { returnTo: globalThis.location.origin },
          });
        }
        logError(error, {
          description: 'Error getting token silently in useApi.sendRequest',
        });
        newState.data = null;
        newState.statusCode = 501;
        newState.error = 'Authentication error';
      }

      if (accessToken) {
        try {
          const { data, statusCode, error } = await Api[command]<T>(
            accessToken,
            url,
            options,
            mock,
          );

          newState.data = data ?? null;
          newState.statusCode = statusCode ?? null;
          newState.error =
            statusCode === 200 || statusCode === 201 ? null : getError(error);
        } catch (error) {
          newState.data = null;
          newState.statusCode = null;
          newState.error = getError(error);
        }
      }

      setState(newState);
      callback?.(newState.data, newState.statusCode, newState.error);
    })();
  };

  return {
    ...state,
    resetData,
    resetError,
    sendRequest,
  };
};

export default useApi;
